import { useEffect, useState, type ReactNode } from "react";
import { Gate as CoralGate, GateBadge, GateError, Icon, usePairingCode } from "@cliqsuite/ui";
import type { GeofenceReason } from "../lib/geofence";
import { detectDevicePlatform, permissionInstructionsFor } from "../lib/permissionInstructions";

// POS lock screens from the canvas (coral-pos-*-gate-*, v2-d-gate-*). Logic lives in StaffGate.

export interface RegisterInfo {
  storeName: string;
  registerName: string;
}

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 15_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** Left-bottom corner: which register this is, and the time. */
function RegisterSide({ register, label = "This register" }: { register: RegisterInfo; label?: string }) {
  const now = useClock();
  return (
    <div className="pos-gate-side">
      <div>
        <small>{label}</small>
        <b>{register.registerName}</b>
        <span>{register.storeName}</span>
      </div>
      <div className="pos-gate-clock">
        <div className="mono">{now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
        <small>{now.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}</small>
      </div>
    </div>
  );
}

function Screen({ register, label, children, wide }: { register: RegisterInfo | null; label?: string; children: ReactNode; wide?: boolean }) {
  return (
    <CoralGate
      app="POS"
      wide={wide}
      side={
        register ? (
          <RegisterSide register={register} label={label} />
        ) : (
          <>
            <b>New register</b>
            <small>Get a code in Owner Portal, under Devices</small>
          </>
        )
      }
    >
      {children}
    </CoralGate>
  );
}

const Heading = ({ title, text }: { title: string; text: ReactNode }) => (
  <div>
    <h1>{title}</h1>
    <p>{text}</p>
  </div>
);

const LinkButton = ({ icon, children, onClick }: { icon: string; children: ReactNode; onClick: () => void }) => (
  <button className="gate-link" onClick={onClick}>
    <Icon name={icon} size={18} />
    {children}
  </button>
);

// ---------------------------------------------------------------------------------------------

export function PairScreen(props: { error: string; pairing: boolean; onPair: (code: string) => void; onClearError: () => void; onReset: () => Promise<void>; waitingSales: number }) {
  const { code, boxes } = usePairingCode({ error: !!props.error, onSubmit: props.onPair, onChange: props.onClearError });
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  return (
    <Screen register={null}>
      <Heading title="Pair this register" text="Type the code from Owner Portal, under Devices." />
      <div>
        <span className="pos-gate-label">Pairing code</span>
        {boxes}
      </div>
      {props.error && <GateError>{props.error}</GateError>}
      <button className="gate-primary" disabled={props.pairing || code.length < 6} onClick={() => props.onPair(code)}>
        {props.pairing ? "Pairing…" : "Pair register"}
        <Icon name="arrow_forward" color="#fff" />
      </button>
      <LinkButton icon="restart_alt" onClick={() => setConfirmReset(true)}>
        Trouble pairing? Reset this device
      </LinkButton>
      {confirmReset && (
        <div className="dlg-back" onClick={() => !resetting && setConfirmReset(false)}>
          <section className="dlg" role="dialog" aria-modal="true" aria-labelledby="reset-t" onClick={(e) => e.stopPropagation()}>
            <GateBadge icon="restart_alt" bg="#FFF1F4" fg="var(--coral)" />
            <h2 id="reset-t">Reset this device?</h2>
            <p>
              {props.waitingSales > 0
                ? `${props.waitingSales} ${props.waitingSales === 1 ? "sale hasn't" : "sales haven't"} reached the server yet. Pair this register again so ${props.waitingSales === 1 ? "it syncs" : "they sync"}; resetting now would lose ${props.waitingSales === 1 ? "it" : "them"}.`
                : "Clears everything saved on this device and reloads fresh from the server. Worth a try if pairing keeps failing."}
            </p>
            <div className="dlg-row">
              <button className="pos-btn-ghost" disabled={resetting} onClick={() => setConfirmReset(false)}>
                Cancel
              </button>
              <button
                className="pos-btn-coral"
                disabled={resetting || props.waitingSales > 0}
                onClick={() => {
                  setResetting(true);
                  void props.onReset();
                }}
              >
                {resetting ? "Resetting…" : "Reset device"}
              </button>
            </div>
          </section>
        </div>
      )}
    </Screen>
  );
}

export interface StaffTile {
  id: string;
  name: string;
  role: string;
  avatarColor: string | null;
}

const initials = (name: string) =>
  name
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const Avatar = ({ staff }: { staff: StaffTile }) => (
  <div className="pos-avatar" style={{ background: staff.avatarColor ?? "var(--ink)" }}>
    {initials(staff.name)}
  </div>
);

export function StaffPickerScreen(props: { register: RegisterInfo; roster: StaffTile[] | null; onPick: (id: string) => void; onUnpair: () => void }) {
  return (
    <Screen register={props.register} wide>
      <Heading title="Who's signing in?" text="Tap your name to enter your PIN." />
      {props.roster === null ? (
        <p className="pos-gate-wait">Loading staff…</p>
      ) : props.roster.length === 0 ? (
        <p className="pos-gate-wait late">No staff are assigned to this store yet. Add them in Owner Portal, under Staff.</p>
      ) : (
        <div className="pos-staff-grid">
          {props.roster.map((s) => (
            <button key={s.id} className="pos-staff" onClick={() => props.onPick(s.id)}>
              <span className="pos-staff-bar" style={{ background: s.avatarColor ?? "var(--ink)" }} />
              <Avatar staff={s} />
              <span className="pos-staff-text">
                <b>{s.name}</b>
                <small>{s.role}</small>
              </span>
            </button>
          ))}
        </div>
      )}
      <LinkButton icon="link_off" onClick={props.onUnpair}>
        Unpair this register
      </LinkButton>
    </Screen>
  );
}

export function PinScreen(props: { register: RegisterInfo; staff: StaffTile; error: string; verifying: boolean; onBack: () => void; onPin: (pin: string) => Promise<void> }) {
  const [pin, setPin] = useState("");
  const press = (d: string) => {
    if (props.verifying) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) void props.onPin(next).finally(() => setPin(""));
  };
  // A hardware keyboard works too.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) press(e.key);
      else if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });
  const failed = !!props.error && pin.length === 0;
  return (
    <Screen register={props.register}>
      <div className="pos-pin-who">
        <button className="pos-icon-btn" aria-label="Back to all staff" onClick={props.onBack}>
          <Icon name="arrow_back" />
        </button>
        <Avatar staff={props.staff} />
        <div>
          <b>{props.staff.name}</b>
          <small>{props.staff.role}</small>
        </div>
      </div>
      <div className="pos-pin-dots">
        <div role={failed ? "alert" : undefined} className={failed ? "err" : ""}>
          {failed ? props.error : props.verifying ? "Checking…" : "Enter your PIN"}
        </div>
        <div aria-label={`${pin.length} of 4 digits entered`}>
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={failed ? "bad" : i < pin.length ? "on" : ""} />
          ))}
        </div>
      </div>
      <div className="pos-keys">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} className="pos-key" onClick={() => press(d)}>
            {d}
          </button>
        ))}
        <span />
        <button className="pos-key" onClick={() => press("0")}>
          0
        </button>
        <button className="pos-key clear" aria-label="Delete" onClick={() => setPin((p) => p.slice(0, -1))}>
          <Icon name="backspace" size={28} color="var(--text2)" />
        </button>
      </div>
    </Screen>
  );
}

export function WaitScreen({ register, text }: { register: RegisterInfo; text: string }) {
  return (
    <Screen register={register}>
      <p className="pos-gate-wait">{text}</p>
    </Screen>
  );
}

export function LocationPromptScreen(props: { register: RegisterInfo; onAllow: () => void; onUnpair: () => void }) {
  return (
    <Screen register={props.register}>
      <GateBadge icon="my_location" bg="#FFF1F4" fg="var(--coral)" />
      <Heading title="Confirm you're at the store" text="Signing in checks your location once, so shifts only start on site." />
      <button className="gate-primary" onClick={props.onAllow}>
        <Icon name="near_me" color="#fff" />
        Allow location
      </button>
      <LinkButton icon="link_off" onClick={props.onUnpair}>
        Unpair this register
      </LinkButton>
    </Screen>
  );
}

type BlockReason = Extract<GeofenceReason, "denied" | "unavailable" | "timeout" | "too_far">;

const km = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);

export function LocationBlockedScreen(props: {
  register: RegisterInfo;
  reason: BlockReason;
  distanceM?: number;
  radiusM?: number;
  onRetry: () => void;
  /** Unpair (at sign-in) or go back (a signed-in clock-in attempt). */
  back: { label: string; icon: string; onClick: () => void };
}) {
  const { reason } = props;
  const steps = reason === "denied" ? permissionInstructionsFor("geolocation", detectDevicePlatform()).steps : null;
  const head =
    reason === "denied"
      ? { icon: "location_off", title: "Location is turned off", text: "This register can't confirm it's at the store. Turn location on, then try again." }
      : reason === "too_far"
        ? {
            icon: "wrong_location",
            title: props.distanceM != null ? `You're ${km(props.distanceM)} away` : "You're not at the store",
            text: `Sign in from ${props.register.storeName}.${props.radiusM ? ` The register only works within ${km(props.radiusM)} of the store.` : ""}`,
          }
        : reason === "timeout"
          ? { icon: "location_searching", title: "Couldn't confirm your location", text: "That took too long. Check your connection and location settings, then try again." }
          : { icon: "location_disabled", title: "This device can't check location", text: "Signing in needs location, which this browser doesn't provide. Try another browser or device." };
  return (
    <Screen register={props.register}>
      <GateBadge icon={head.icon} bg="#FFF1F4" fg="var(--coral)" />
      <Heading title={head.title} text={head.text} />
      {steps && (
        <ol className="pos-steps">
          {steps.map((s, i) => (
            <li key={i}>
              <span>{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>
      )}
      <button className="gate-primary" onClick={props.onRetry}>
        <Icon name="refresh" color="#fff" />
        {reason === "too_far" ? "Check again" : "Try again"}
      </button>
      <LinkButton icon={props.back.icon} onClick={props.back.onClick}>
        {props.back.label}
      </LinkButton>
    </Screen>
  );
}

export function NotEnabledScreen({ register, onUnpair }: { register: RegisterInfo; onUnpair: () => void }) {
  return (
    <Screen register={register}>
      <GateBadge icon="widgets" bg="var(--amber-soft)" fg="var(--amber)" />
      <Heading
        title="POS isn't turned on"
        text="This business doesn't have the POS module. Turn it on in Owner Portal under Modules, or pair this device with a POS store."
      />
      <button className="pos-btn-ghost tall" onClick={onUnpair}>
        <Icon name="link_off" />
        Unpair this device
      </button>
    </Screen>
  );
}

export function UnpairedScreen({ register, waitingSales, onPairAgain }: { register: RegisterInfo; waitingSales: number; onPairAgain: () => void }) {
  return (
    <Screen register={register} label="This register was">
      <GateBadge icon="link_off" bg="var(--pink)" fg="var(--coral)" />
      <Heading
        title="This register was unpaired"
        text={`It was removed from ${register.storeName} in Owner Portal. Nobody can sign in here until it's paired again.`}
      />
      <div className="gate-facts">
        {waitingSales > 0 ? (
          <div>
            <Icon name="sync_problem" size={18} color="var(--amber)" />
            {waitingSales} {waitingSales === 1 ? "sale hasn't" : "sales haven't"} reached the server yet. They're kept here and send once it's paired again.
          </div>
        ) : (
          <div>
            <Icon name="cloud_done" size={18} color="var(--ok)" />
            All sales on this register reached the server
          </div>
        )}
        <div>
          <Icon name="delete_sweep" size={18} color="var(--text2)" />
          Menu, staff and sales were cleared from this device
        </div>
      </div>
      <button className="gate-primary" onClick={onPairAgain}>
        <Icon name="add_link" color="#fff" />
        Pair this register again
      </button>
    </Screen>
  );
}

export function PlanEndedScreen(props: { register: RegisterInfo; businessName: string; waitingSales: number; onCheckAgain: () => void; onSignOut?: () => void }) {
  return (
    <Screen register={props.register} label="Plan ended">
      <GateBadge icon="event_busy" bg="var(--amber-soft)" fg="var(--amber)" />
      <Heading
        title={`${props.businessName}'s plan has ended`}
        text="Ask the owner to renew in Owner Portal, under Billing & plan. The register opens again as soon as it's paid."
      />
      <div className="gate-facts">
        <div>
          <Icon name="lock" size={18} color="var(--text2)" />
          New sales are paused on every register
        </div>
        <div>
          <Icon name="cloud_done" size={18} color="var(--ok)" />
          Past sales and bills are safe
        </div>
        {props.waitingSales > 0 && (
          <div>
            <Icon name="sync" size={18} color="var(--amber)" />
            {props.waitingSales} {props.waitingSales === 1 ? "sale" : "sales"} from before it ended {props.waitingSales === 1 ? "is" : "are"} waiting to sync
          </div>
        )}
      </div>
      <button className="gate-primary" onClick={props.onCheckAgain}>
        <Icon name="refresh" color="#fff" />
        Check again
      </button>
      {props.onSignOut && (
        <LinkButton icon="logout" onClick={props.onSignOut}>
          Sign out
        </LinkButton>
      )}
    </Screen>
  );
}
